import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { faTrash } from '@fortawesome/free-solid-svg-icons';

import { ChatWidgetInputBase } from '../chat-widget-input-base';

export interface ChatEmailAnswer {
  emails: string[];
}

const MAX_EMAILS = 3;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Renders the additional-email picker widget inside the chat widget: one
 * text field per address (up to 3), with a "+ New email" button that only
 * reveals the next field once the current last one holds a genuinely valid
 * address. Mirrors the other widgets' locked/read-only + showLabel/Skip
 * behaviour exactly.
 */
@Component({
  selector: 'app-chat-email-input',
  standalone: false,
  templateUrl: './chat-email-input.component.html',
  styleUrls: ['./chat-email-input.component.scss']
})
export class ChatEmailInputComponent extends ChatWidgetInputBase implements OnChanges {
  faTrash = faTrash;

  @Input() passengerName = '';
  // Deliberately typed as the raw record (not a pre-cast interface) and read
  // defensively inside ngOnChanges instead — the parent must pass the SAME
  // object reference across change-detection cycles (e.g.
  // `passenger.savedValue` directly) rather than constructing a new object
  // literal on every template check, otherwise `ngOnChanges` re-fires on
  // every keystroke and resets whatever the user is currently typing.
  @Input() savedValue: Record<string, unknown> | null = null;
  @Input() required?: boolean;
  @Output() answer = new EventEmitter<ChatEmailAnswer>();
  @Output() skip = new EventEmitter<void>();

  /** One entry per visible text field — always at least one, up to MAX_EMAILS. */
  emailInputs: string[] = [''];
  /** Human-readable summary of what was submitted (via Continue or Skip),
   * shown in place of the controls once this widget instance is locked. */
  chosenSummary: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    // Reset the widget whenever the passenger changes (new question) — but
    // NOT merely because `locked` toggled, which would wipe out the
    // selection just as we want to display it as read-only.
    if (this.onlyLockedChanged(changes)) {
      return;
    }
    const saved = this.savedEmails;
    this.emailInputs = saved.length ? [...saved] : [''];
    this.chosenSummary = null;
  }

  private get savedEmails(): string[] {
    const emails = this.savedValue?.['emails'];
    return Array.isArray(emails) ? emails.filter((e): e is string => typeof e === 'string') : [];
  }

  get isMandatory(): boolean {
    return this.required === true;
  }

  isValidEmail(value: string): boolean {
    return EMAIL_RE.test(value.trim());
  }

  /** The "+ New email" button only appears/works once the last visible field
   * holds a genuinely valid address, and never past MAX_EMAILS fields. */
  get canAddMore(): boolean {
    if (this.emailInputs.length >= MAX_EMAILS) {
      return false;
    }
    const last = this.emailInputs[this.emailInputs.length - 1] ?? '';
    return this.isValidEmail(last);
  }

  get isValid(): boolean {
    const nonEmpty = this.emailInputs.map((e) => e.trim()).filter((e) => e.length > 0);
    if (nonEmpty.length === 0) {
      // No address entered at all is fine unless this field is mandatory
      // (in practice additionalEmail never is, but keep the same pattern
      // as the other optional widgets).
      return !this.isMandatory;
    }
    // Every non-empty field must be a genuinely valid address.
    return this.emailInputs.every((e) => !e.trim() || this.isValidEmail(e));
  }

  addEmailField(): void {
    if (this.locked || !this.canAddMore) {
      return;
    }
    this.emailInputs.push('');
  }

  /** Removes one of the open text fields (only shown/usable when there's more
   * than one, so there's always at least one field left). */
  removeEmailField(index: number): void {
    if (this.locked || this.emailInputs.length <= 1) {
      return;
    }
    this.emailInputs.splice(index, 1);
  }

  onContinue(): void {
    if (this.locked || !this.isValid) {
      return;
    }
    const emails = this.emailInputs
      .map((e) => e.trim())
      .filter((e) => e.length > 0 && this.isValidEmail(e));
    if (emails.length) {
      this.chosenSummary = emails.join(', ');
      this.answer.emit({ emails });
      return;
    }
    // Only reachable for an optional field with nothing entered.
    this.chosenSummary = 'Skipped';
    this.skip.emit();
  }

  onSkip(): void {
    if (this.locked) {
      return;
    }
    this.chosenSummary = 'Skipped';
    this.skip.emit();
  }
}
